/**
 * SuperBrain Agentic Aggregate - Main App Component
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 * Faith. Service. Integrity.
 */

import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import Disclaimer from './components/Disclaimer';
import DMCA from './components/DMCA';
import AdminFeedback from './components/AdminFeedback';
import PricingPage from './components/PricingPage';
import BuilderPage from './components/BuilderPage';
import Settings from './components/Settings';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import CancelDeletionPage from './components/CancelDeletionPage';
import StatusPage from './components/StatusPage';
import ReferralRedirect from './components/ReferralRedirect';
import ConsentModal from './components/ConsentModal';
import LegalFooter from './components/LegalFooter';
import AuthPage from './components/AuthPage';
import './App.css';

function App() {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    return null;
  });

  const handleAuthenticated = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <Router>
      <div className="App">
        <ConsentModal />
        <Routes>
          {/* Legal Pages - accessible to everyone */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/dmca" element={<DMCA />} />
          
          {/* Pricing Page - accessible to everyone */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Password recovery — accessible to everyone, both authed & not */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage onAuthenticated={handleAuthenticated} />}
          />
          <Route path="/cancel-deletion" element={<CancelDeletionPage />} />
          <Route path="/status" element={<StatusPage />} />

          {/* Referral entry - stores code + redirects to homepage */}
          <Route path="/r/:code" element={<ReferralRedirect />} />
          
          {/* Admin Feedback Page - admin only */}
          <Route
            path="/admin/feedback"
            element={
              user ? (
                <AdminFeedback onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* SuperBrain Builder - authed users */}
          <Route
            path="/builder"
            element={
              user ? (
                <BuilderPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* Main App Routes */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthPage onAuthenticated={handleAuthenticated} initialMode="login" />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <LandingPage onAuthenticated={handleAuthenticated} />
              )
            }
          />
          
          <Route
            path="/dashboard"
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/settings"
            element={
              user ? (
                <Settings user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <LegalFooter />
      </div>
    </Router>
  );
}

export default App;
